        import { MantineThemeOverride, ColorScheme } from "@mantine/core";

// Define the theme interface
export interface ThemeConfig {
  primaryColor: string; // Must be a key from Mantine's color palette
  secondaryColor: string; // Must be a key from Mantine's color palette
  borderRadius: number;
  fontFamily: string;
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  shadows: {
    sm: string;
    md: string;
    lg: string;
  };
  // Custom colors for components that need direct hex values
  customColors: {
    primary: string;
    secondary: string;
  };
}

// Default theme configuration
export const defaultThemeConfig: ThemeConfig = {
  primaryColor: "blue", // This must be a key in Mantine's colors
  secondaryColor: "gray", // This must be a key in Mantine's colors
  borderRadius: 8,
  fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
  },
  shadows: {
    sm: "0 1px 3px rgba(0, 0, 0, 0.1)",
    md: "0 4px 6px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px rgba(0, 0, 0, 0.1)",
  },
  // Custom colors for direct hex value usage
  customColors: {
    primary: "#228be6", // Default blue
    secondary: "#868e96", // Default gray
  },
};

// Helper function to generate a color palette from a base color
function generateColorPalette(baseColor: string): [string, string, string, string, string, string, string, string, string, string] {
  // Simple palette generation with exactly 10 elements for Mantine
  return [
    "#f8f9fa", // Lightest shade (0)
    "#e9ecef", // (1)
    "#dee2e6", // (2)
    "#ced4da", // (3)
    "#adb5bd", // (4)
    baseColor,  // Base color (5)
    "#495057", // (6)
    "#343a40", // (7)
    "#212529", // (8)
    "#121416", // Darkest shade (9)
  ];
}

// Create a Mantine theme from our theme config
export function createMantineTheme(
  themeConfig: ThemeConfig = defaultThemeConfig,
  colorScheme: ColorScheme = "light"
): MantineThemeOverride {
  // Use default theme config if none provided
  const config = themeConfig || defaultThemeConfig;
  
  // Ensure customColors exists to prevent errors
  const customColors = config.customColors || {
    primary: "#228be6", // Default blue
    secondary: "#fa5252", // Default red - more visible
  };
  
  // Ensure primaryColor is a valid Mantine color name
  const validColors = ["blue", "indigo", "purple", "pink", "red", "orange", 
                      "yellow", "lime", "green", "teal", "cyan", "gray", "dark"];
  
  const primaryColor = validColors.includes(config.primaryColor) 
    ? config.primaryColor 
    : "blue";
  
  return {
    colorScheme,
    primaryColor, // This must be a key in Mantine's default colors
    fontFamily: config.fontFamily,
    defaultRadius: config.borderRadius,
    shadows: {
      sm: config.shadows.sm,
      md: config.shadows.md,
      lg: config.shadows.lg,
    },
    spacing: config.spacing,
    components: {
      Modal: {
        defaultProps: {
          padding: "xl",
        },
        styles: {
          title: {
            fontSize: "1.2rem",
            fontWeight: 600,
          },
        },
      },
      ModalRoot: {
        defaultProps: {
          centered: true,
        },
      },
      Overlay: {
        defaultProps: {
          opacity: 0.6,
          blur: 6,
        },
      },
      InputWrapper: {
        styles: {
          label: {
            marginBottom: 4,
          },
        },
      },
      Code: {
        styles: (theme) => ({
          root: {
            fontSize: theme.fontSizes.sm,
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.colors.dark[7]
                : theme.colors.gray[1],
          },
        }),
      },
    },
    globalStyles: (theme) => ({
      body: {
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[0],
      },
      // Add CSS variables for secondary color
      ":root": {
        "--secondary-color": customColors.secondary,
        "--secondary-color-light": "#ced4da", // Lighter shade (index 3)
        "--secondary-color-dark": "#495057", // Darker shade (index 6)
      },
    }),
  };
}
